"use client";

import { useState, useCallback, useMemo } from "react";
import { Loader2, Check, AlertCircle, Info } from "lucide-react";
import type { ResourceSchema, ResourceField, FormState, ResourceFormRendererProps } from "./types";
import { FieldRenderer } from "./FieldRenderer";

/**
 * ResourceFormRenderer - Main component for rendering dynamic resource forms
 *
 * Features:
 * - Deep nesting support via recursive FieldRenderer
 * - GroupVariable support with is_list (repeatable rows)
 * - Collapsible sections for complex schemas
 * - Real-time validation
 * - View/Edit/Create modes
 */
export function ResourceFormRenderer({
  schema,
  initialValues = {},
  onSubmit,
  onCancel,
  onValidate,
  mode = "create",
  isLoading = false,
}: ResourceFormRendererProps) {
  // Form state
  const [values, setValues] = useState<Record<string, unknown>>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    message?: string;
  } | null>(null);

  // Compute if form is dirty
  const isDirty = useMemo(() => {
    return JSON.stringify(values) !== JSON.stringify(initialValues);
  }, [values, initialValues]);

  // Handle field change
  const handleChange = useCallback((path: string, value: unknown) => {
    setValues((prev) => {
      const newValues = { ...prev };
      setNestedValue(newValues, path, value);
      return newValues;
    });
    setTouched((prev) => ({ ...prev, [path]: true }));
    setValidationResult(null); // Clear validation on change
  }, []);

  // Validate required fields
  const validateRequired = useCallback((): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    const validateFields = (fields: ResourceField[], parentPath: string = "") => {
      for (const field of fields) {
        const path = parentPath ? `${parentPath}.${field.name}` : field.name;
        const value = getNestedValue(values, path);

        if (field.required) {
          if (value === undefined || value === null || value === "") {
            newErrors[path] = `${field.display_name} is required`;
          }
        }

        // Recurse into groups
        if (field.type === "group" && "fields" in field) {
          validateFields(field.fields, path);
        }
      }
    };

    validateFields(schema.fields);
    return newErrors;
  }, [schema.fields, values]);

  // Handle validate button
  const handleValidate = useCallback(async () => {
    // First check required fields
    const requiredErrors = validateRequired();
    if (Object.keys(requiredErrors).length > 0) {
      setErrors(requiredErrors);
      setValidationResult({ valid: false, message: "Please fill in all required fields" });
      return;
    }

    if (onValidate) {
      setIsValidating(true);
      try {
        const result = await onValidate(values);
        setErrors(result.errors || {});
        setValidationResult({
          valid: result.valid,
          message: result.valid ? "Configuration is valid" : "Validation failed",
        });
      } catch (err) {
        setValidationResult({
          valid: false,
          message: err instanceof Error ? err.message : "Validation error",
        });
      } finally {
        setIsValidating(false);
      }
    } else {
      setErrors({});
      setValidationResult({ valid: true, message: "Client-side validation passed" });
    }
  }, [values, onValidate, validateRequired]);

  // Handle submit
  const handleSubmit = useCallback(async () => {
    const requiredErrors = validateRequired();
    if (Object.keys(requiredErrors).length > 0) {
      setErrors(requiredErrors);
      return;
    }

    onSubmit(values);
  }, [values, onSubmit, validateRequired]);

  // Group fields by category for better organization
  const { requiredFields, optionalFields } = useMemo(() => {
    const required: ResourceField[] = [];
    const optional: ResourceField[] = [];

    for (const field of schema.fields) {
      if (field.required) {
        required.push(field);
      } else {
        optional.push(field);
      }
    }

    return { requiredFields: required, optionalFields: optional };
  }, [schema.fields]);

  return (
    <div className="space-y-3 text-xs">
      {/* Header */}
      <div className="border-b border-border pb-2">
        <h2 className="text-sm font-semibold text-foreground">{schema.display_name}</h2>
        {schema.description && (
          <p className="mt-1 text-xs text-muted-foreground">{schema.description}</p>
        )}
        <div className="mt-1 flex items-center gap-2">
          <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground">
            {schema.type.split(".").pop()}
          </span>
          {schema.category && (
            <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground">
              {schema.category}
            </span>
          )}
        </div>
      </div>

      {/* Agent Guidance - hidden in compact mode */}
      {schema.agent_guidance && mode === "create" && (
        <div className="rounded border border-blue-800/50 bg-blue-900/20 p-2">
          <div className="flex items-start gap-2">
            <Info className="h-3 w-3 text-blue-400 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-blue-300">Setup Guide</p>
              <ol className="mt-1 text-[10px] text-blue-200/70 list-decimal list-inside space-y-0.5">
                {schema.agent_guidance.required_steps.slice(0, 3).map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* Required Fields Section */}
      {requiredFields.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
            Required
          </h3>
          <div className="grid gap-2">
            {requiredFields.map((field) => (
              <FieldRenderer
                key={field.name}
                field={field}
                value={getNestedValue(values, field.name)}
                onChange={handleChange}
                path=""
                depth={0}
                errors={errors}
                mode={mode}
                allValues={values}
              />
            ))}
          </div>
        </div>
      )}

      {/* Optional Fields Section */}
      {optionalFields.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
            Optional
          </h3>
          <div className="grid gap-2">
            {optionalFields.map((field) => (
              <FieldRenderer
                key={field.name}
                field={field}
                value={getNestedValue(values, field.name)}
                onChange={handleChange}
                path=""
                depth={0}
                errors={errors}
                mode={mode}
                allValues={values}
              />
            ))}
          </div>
        </div>
      )}

      {/* Validation Result */}
      {validationResult && (
        <div
          className={`flex items-center gap-2 rounded p-3 ${
            validationResult.valid
              ? "border border-emerald-800/50 bg-emerald-900/20 text-emerald-300"
              : "border border-red-800/50 bg-red-900/20 text-red-300"
          }`}
        >
          {validationResult.valid ? (
            <Check className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          <span className="text-sm">{validationResult.message}</span>
        </div>
      )}

      {/* Actions */}
      {mode !== "view" && (
        <div className="flex items-center gap-2 border-t border-border pt-2">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
            className="flex items-center gap-1 rounded bg-emerald-600 px-2 py-1 text-xs text-white hover:bg-emerald-500 disabled:opacity-50"
          >
            {isLoading && <Loader2 className="h-3 w-3 animate-spin" />}
            {mode === "create" ? "Create" : "Save"}
          </button>

          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="rounded px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
          )}

          {isDirty && (
            <span className="ml-auto text-[10px] text-amber-500">Unsaved</span>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  if (!path) return obj;
  return path.split(".").reduce((acc: unknown, key) => {
    if (acc && typeof acc === "object") {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

function setNestedValue(
  obj: Record<string, unknown>,
  path: string,
  value: unknown
): void {
  const keys = path.split(".");
  let current = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    // Handle array notation like "rows[0]"
    const arrayMatch = key.match(/^(.+)\[(\d+)\]$/);
    if (arrayMatch) {
      const arrayKey = arrayMatch[1];
      const index = parseInt(arrayMatch[2]);
      if (!Array.isArray(current[arrayKey])) {
        current[arrayKey] = [];
      }
      const arr = current[arrayKey] as unknown[];
      if (!arr[index]) {
        arr[index] = {};
      }
      current = arr[index] as Record<string, unknown>;
    } else {
      if (!(key in current) || typeof current[key] !== "object") {
        current[key] = {};
      }
      current = current[key] as Record<string, unknown>;
    }
  }

  const lastKey = keys[keys.length - 1];
  const arrayMatch = lastKey.match(/^(.+)\[(\d+)\]$/);
  if (arrayMatch) {
    const arrayKey = arrayMatch[1];
    const index = parseInt(arrayMatch[2]);
    if (!Array.isArray(current[arrayKey])) {
      current[arrayKey] = [];
    }
    (current[arrayKey] as unknown[])[index] = value;
  } else {
    current[lastKey] = value;
  }
}

// =============================================================================
// EXPORT
// =============================================================================

export { FieldRenderer } from "./FieldRenderer";
export type * from "./types";
